import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import type { AxiosResponse } from 'axios'
import {
  PODPISLON_NOT_CONNECTED_TOOLTIP,
  SEND_FOR_SIGNATURE_NOT_REQUIRED_TOOLTIP,
  SEND_FOR_SIGNATURE_PDF_ONLY_TOOLTIP,
  SEND_FOR_SIGNATURE_PENDING_TOOLTIP,
  SEND_FOR_SIGNATURE_SIGNED_TOOLTIP,
  SEND_FOR_SIGNATURE_TOOLTIP,
} from './constants'
import {
  isContractNotRequiredSigning,
  isContractPendingSignature,
  isContractPdfFile,
} from './contractSigningState.utils'

export type RecipientMode = 'student' | 'parent' | 'manual'

export type SendForSignatureContractSource = {
  file_path?: string | null
  path?: string | null
  signing_state?: string | null
}

export {
  isContractNotRequiredSigning,
  isContractPendingSignature,
} from './contractSigningState.utils'

export const getSendForSignatureTooltip = (
  contract: SendForSignatureContractSource,
  podpislonReady: boolean,
  isPodpislonChecking = false,
): string => {
  if (isPodpislonChecking) {
    return ''
  }

  if (!podpislonReady) {
    return PODPISLON_NOT_CONNECTED_TOOLTIP
  }

  if (!isContractPdfFile(contract?.file_path ?? contract?.path)) {
    return SEND_FOR_SIGNATURE_PDF_ONLY_TOOLTIP
  }

  if (contract?.signing_state === 'signed') {
    return SEND_FOR_SIGNATURE_SIGNED_TOOLTIP
  }

  if (isContractPendingSignature(contract?.signing_state)) {
    return SEND_FOR_SIGNATURE_PENDING_TOOLTIP
  }

  if (isContractNotRequiredSigning(contract?.signing_state)) {
    return SEND_FOR_SIGNATURE_NOT_REQUIRED_TOOLTIP
  }

  return SEND_FOR_SIGNATURE_TOOLTIP
}

export const canSendContractForSignature = (
  contract: SendForSignatureContractSource,
  podpislonReady: boolean,
  isPodpislonChecking = false,
): boolean =>
  !isPodpislonChecking &&
  podpislonReady &&
  isContractPdfFile(contract?.file_path ?? contract?.path) &&
  contract?.signing_state !== 'signed' &&
  !isContractPendingSignature(contract?.signing_state) &&
  !isContractNotRequiredSigning(contract?.signing_state)

export type SendForSignaturePayload = {
  recipient_type: RecipientMode
  parent_id?: number
  manual_signer_name?: string
  manual_signer_phone?: string
  no_sms?: boolean
}

export const buildSendForSignaturePayload = ({
  recipientMode,
  parentId,
  manualSignerName,
  manualSignerPhone,
}: {
  recipientMode: RecipientMode
  parentId?: number | null
  manualSignerName?: string
  manualSignerPhone?: string
}): SendForSignaturePayload => {
  if (recipientMode === 'parent' && parentId) {
    return {
      recipient_type: 'parent',
      parent_id: parentId,
    }
  }

  if (recipientMode === 'manual') {
    return {
      recipient_type: 'manual',
      manual_signer_name: manualSignerName?.trim(),
      manual_signer_phone: manualSignerPhone?.trim(),
    }
  }

  return {
    recipient_type: 'student',
  }
}

export const parseSendForSignatureError = (error: unknown): string => {
  const response = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data

  if (!response) {
    return 'Не удалось отправить договор на подпись'
  }

  if (response.meta && typeof response.meta === 'object') {
    const meta = response.meta as { type?: string }
    if (meta.type === 'podpislon_insufficient_balance') {
      const errorList = response.error
      if (Array.isArray(errorList) && typeof errorList[0] === 'string') {
        return errorList[0]
      }
      return 'Для отправки документов не хватает баланса на сервисе Подпислон.'
    }
  }

  const errorList = response.error
  if (Array.isArray(errorList) && errorList.length) {
    const first = errorList[0]
    if (typeof first === 'string') {
      return first
    }
    if (first && typeof first === 'object') {
      const messages = Object.values(first as Record<string, string[]>)
        .flat()
        .filter(Boolean)
      if (messages[0]) {
        return String(messages[0])
      }
    }
  }

  if (typeof response.message === 'string' && response.message) {
    return response.message
  }

  return 'Не удалось отправить договор на подпись'
}

export const isSendForSignatureFailure = (response: unknown): boolean => {
  if (!response || typeof response !== 'object') {
    return true
  }

  if (
    'isAxiosError' in response &&
    Boolean((response as { isAxiosError?: boolean }).isAxiosError)
  ) {
    return true
  }

  return isError(response as AxiosResponse<ResponseWrapper>)
}

export const getSendForSignatureFailureMessage = (response: unknown): string => {
  if (
    response &&
    typeof response === 'object' &&
    'isAxiosError' in response &&
    (response as { isAxiosError?: boolean }).isAxiosError
  ) {
    return parseSendForSignatureError(response)
  }

  const axiosResponse = response as AxiosResponse<ResponseWrapper>
  return parseSendForSignatureError({
    response: {
      data: axiosResponse?.data as Record<string, unknown>,
    },
  })
}

export const extractSendForSignatureResponse = (
  response: { data?: Record<string, unknown> } | undefined,
) => {
  const payload = response?.data
  if (!payload) {
    return null
  }

  if (payload.data && typeof payload.data === 'object') {
    return payload.data as Record<string, unknown>
  }

  return payload as Record<string, unknown>
}

export const isPodpislonIntegrationReady = (
  fields?: {
    api_key?: string | null
    is_active?: boolean | null
    is_connected?: boolean | null
  } | null,
) => Boolean(fields?.is_active && fields?.is_connected && fields?.api_key)
